// Generic reader of Brazilian credit card statements. Two layouts are understood, page by page:
// - inline: "DD/MM DESCRIPTION VALUE", possibly with more than one column of transactions per line;
// - columns (e.g. Original/PicPay): the dates of the page come first, one per line, and the
//   "DESCRIPTION VALUE" lines of the "LANÇAMENTOS" sections come later, in the same order.

export type ParsedItemKind = 'purchase' | 'credit' | 'payment';

export interface ParsedCardItem {
  date: string,
  description: string,
  value: number,
  kind: ParsedItemKind,
  installment: number | null,
  installments: number | null
}

export interface ParsedStatement {
  items: ParsedCardItem[],
  total: number | null,
  dueDate: string | null
}

const MONTHS = 'JAN|FEV|MAR|ABR|MAI|JUN|JUL|AGO|SET|OUT|NOV|DEZ';
// A date not glued to the previous word, so "PARC02/04" is not a date
const DATE = new RegExp(String.raw`(?<![\w/])(\d{2}\/\d{2}(?:\/\d{2,4})?|\d{2}\s(?:${MONTHS})[A-Z]*)(?=\s)`, 'gi');
const VALUE = /(-\s*)?(?:R\$\s*)?(-\s*)?(\d{1,3}(?:\.\d{3})*,\d{2})(\s*-)?(?=\s|$)/g;
// Foreign currency amounts that precede the value in BRL, e.g. "10,00 USD"
const TRAILING_AMOUNTS = /(?:\s+(?:R\$|US\$|USD|EUR|€)?\s*-?\s*\d{1,3}(?:\.\d{3})*,\d{2}\s*(?:USD|EUR)?)+\s*$/i;
const NOT_ITEMS = /^(TOTAL|SALDO|LIMITE|VALOR|VENCIMENTO|PAGAMENTO M[IÍ]NIMO|ENCARGOS PREVISTOS|CR[EÉ]DITO ROTATIVO)/i;
const PAYMENT = /\b(PAGAMENTO|PAGTO|PAG\s+FATURA)\b/i;

// Same as PAGE_BREAK of utils/pdf-text, kept here so the parser has no pdf.js dependency
const PAGE_BREAK = '\f';
const DATE_LINE = /^(\d{2}\/\d{2})$/;
const ITEM_LINE = /^(.+?)\s+(-\s*)?(\d{1,3}(?:\.\d{3})*,\d{2})(\s*-)?$/;
const SECTION_START = /^LAN[ÇC]AMENTOS\b/i;
const SECTION_END = /^(SUBTOTAL|TOTAL|SALDO)\b/i;

// Genial: labels in one line and values in the next, the total may be negative (credit balance)
// "Valor total da fatura Vencimento Pagamento mínimo R$ - 408,00 25/09/26 R$ 0,00"
const GENIAL_HEADER = /valor total da fatura\s+vencimento\s+pagamento m[ií]nimo\s+R\$\s*(-)?\s*(\d{1,3}(?:\.\d{3})*,\d{2})\s+(\d{2}\/\d{2}\/\d{2,4})/i;

// First match wins; "Total da fatura anterior" is not the total of this statement
const TOTALS = [
  /saldo total\s+(?:R\$\s*)?(\d{1,3}(?:\.\d{3})*,\d{2})/i,
  /valor total da fatura\s+(?:R\$\s*)?(\d{1,3}(?:\.\d{3})*,\d{2})/i,
  /total\s+(?:desta\s+|da\s+)?fatura(?!\s+anterior)[^0-9]{0,30}(\d{1,3}(?:\.\d{3})*,\d{2})/i,
  /pagamento total\s+(?:R\$\s*)?(\d{1,3}(?:\.\d{3})*,\d{2})/i
];

export function parseCardStatement(text: string): ParsedStatement {
  const items: ParsedCardItem[] = [];
  // A "LANÇAMENTOS" section may continue on the next page
  const columns = { inSection: false };

  for (const page of text.split(PAGE_BREAK)) {
    const lines = page.split('\n').map(line => line.replace(/\s+/g, ' ').trim()).filter(line => line);
    const inline = lines.flatMap(parseLine);
    const columnItems = parseColumns(lines, columns);
    items.push(...(inline.length ? inline : columnItems));
  }

  const flat = text.replace(/\s+/g, ' ');
  const header = GENIAL_HEADER.exec(flat);
  if (header)
    return { items, total: parseBrl(header[2]) * (header[1] ? -1 : 1), dueDate: header[3] };

  const total = TOTALS.map(regex => flat.match(regex)).find(match => match);
  const dueDate = flat.match(/vencimento[^0-9]{0,20}(\d{2}\/\d{2}\/\d{2,4})/i);

  return {
    items,
    total: total ? parseBrl(total[1]) : null,
    dueDate: dueDate?.[1] ?? null
  };
}

// Dates and transactions of one page, paired in order; dates are left empty when the counts differ
function parseColumns(lines: string[], state: { inSection: boolean }): ParsedCardItem[] {
  const dates: string[] = [];
  const items: ParsedCardItem[] = [];

  for (const line of lines) {
    const date = DATE_LINE.exec(line);
    if (date) dates.push(date[1]);
    else if (SECTION_START.test(line)) state.inSection = true;
    else if (SECTION_END.test(line)) state.inSection = false;
    else if (state.inSection) {
      const match = ITEM_LINE.exec(line);
      const item = match && buildItem('', match[1], !!(match[2] || match[4]), match[3]);
      if (item) items.push(item);
    }
  }

  if (dates.length == items.length) items.forEach((item, i) => item.date = dates[i]);
  return items;
}

function parseLine(line: string): ParsedCardItem[] {
  const starts = [...line.matchAll(DATE)].map(m => m.index!);
  if (!starts.length) return [];

  // One segment per date; a segment without value was a date-like text of the description
  const segments: string[] = [];
  let pending = '';
  starts.forEach((start, i) => {
    const segment = pending + line.slice(start, starts[i + 1] ?? line.length);
    if (lastValue(segment)) {
      segments.push(segment.trim());
      pending = '';
    } else {
      pending = segment;
    }
  });

  return segments.map(parseSegment).filter((item): item is ParsedCardItem => item != null);
}

function parseSegment(segment: string): ParsedCardItem | null {
  const date = new RegExp(DATE.source, 'i').exec(segment)![1];
  const rest = segment.slice(segment.indexOf(date) + date.length).trim();
  const value = lastValue(rest);
  if (!value) return null;

  return buildItem(date, rest.slice(0, value.index), !!(value[1] || value[2] || value[4]), value[3]);
}

function buildItem(date: string, text: string, negative: boolean, value: string): ParsedCardItem | null {
  const description = text.replace(TRAILING_AMOUNTS, '').replace(/\s*R\$\s*$/, '').trim();
  if (!description || NOT_ITEMS.test(description)) return null;

  const amount = parseBrl(value) * (negative ? -1 : 1);
  const installment = parseInstallment(description);

  return {
    date,
    description,
    value: amount,
    kind: PAYMENT.test(description) ? 'payment' : amount < 0 ? 'credit' : 'purchase',
    installment: installment?.installment ?? null,
    installments: installment?.installments ?? null
  };
}

function lastValue(text: string): RegExpExecArray | null {
  let last: RegExpExecArray | null = null;
  for (const match of text.matchAll(VALUE)) last = match as RegExpExecArray;
  return last;
}

export function parseBrl(value: string): number {
  return Number(value.replace(/\./g, '').replace(',', '.'));
}

// "MERCADOLIVRE*2PARC02/04", "PARC 3/10" or "PARCELA 2 DE 4" -> { installment: 2, installments: 4 }
export function parseInstallment(description: string): { installment: number, installments: number } | null {
  const match = description.match(/PARC(?:ELA)?\s*(\d{1,2})\s*(?:\/|DE)\s*(\d{1,2})/i);
  return match ? { installment: Number(match[1]), installments: Number(match[2]) } : null;
}

// Name of the purchase without the installment, e.g. "MERCADOLIVRE*2PARC02/04" -> "MERCADOLIVRE"
export function baseDescription(description: string): string {
  const base = description.replace(/\*?\d*\s*PARC(?:ELA)?\s*\d{1,2}\s*(?:\/|DE)\s*\d{1,2}.*$/i, '').replace(/\s+/g, ' ').trim();
  return (base || description).toUpperCase();
}

// Category of the same purchase in previous statements, otherwise "Parcelado" for installments
export function suggestCategory(
  item: { description: string, installment: number | null },
  history: { description: string, category: string }[],
  categories: string[]
): string {
  const base = baseDescription(item.description);
  const previous = history.find(h => h.category && baseDescription(h.description) == base);
  if (previous && categories.includes(previous.category)) return previous.category;

  const installments = categories.find(c => c.toLowerCase() == 'parcelado');
  return item.installment && installments ? installments : '';
}
